import type { IconName } from '../components/ui/Icon';
import type { Tone } from '../components/ui/primitives/cx';
import type { AppSection } from '../layouts/navConfig';
import type { UserRole } from '../types/auth';
import type {
  HomeFinancial,
  HomeLowStock,
  HomeWeekDay,
  InsightCategory,
  LowStockItem,
  OverviewToday,
  OwnerOverview,
  PendingSummary,
} from '../types/dashboard';
import { lowStockRowView } from './homeSummary';
import { APPROXIMATE_COST_NOTE } from './otherSaleCosting';
import { confidenceView, formatMoney, insightsView, periodLabel, todayView, weekdayLabel } from './ownerOverview';

/**
 * Rediseño · Bloque C — lógica de presentación PURA del Inicio (sin React, testeable en node).
 *
 * El Inicio nuevo reorganiza lo que el servidor YA entrega; aquí se decide qué frase y qué estado
 * lleva cada bloque. Tres reglas que este archivo no puede romper:
 *
 *   1. Ninguna cifra de dinero se calcula aquí. Todo importe llega del servidor y solo se le da
 *      formato. Lo único que se compone es el ANCHO de dos barras (la de la semana y la del punto de
 *      equilibrio), que no son cifras: son dibujo.
 *   2. Un dato que no existe no se inventa: no hay ticket promedio, ni insights de descubrimiento,
 *      ni «¢ de cada peso». Lo que falta está listado en `docs/UI_REDESIGN_BLOCK_C.md`.
 *   3. El resultado estimado pasa SIEMPRE por su sobre de confianza (`confidenceView`): sin sobre
 *      no hay número, y sus cuatro estados no se presentan con la misma certeza (B0-01).
 */

const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

// ─────────────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────────────

export interface HeroView {
  /** «Hola, Carmen». El saludo de mano (👋) lo pone el componente, como adorno. */
  greeting: string;
  /** El apoyo del saludo: copy aprobado (P-021, P-030). */
  lead: string;
  /** «Hoy · martes 14 de mayo». */
  dateLabel: string;
}

/**
 * Hero aprobado (B0-05): saludo y fecha. La foto del negocio no existe como dato (P-032, B1) y no
 * se dibuja. La segunda frase habla de cifras del negocio: quien no las ve —un EMPLOYEE, que recibe
 * 204 del servidor— se queda con el saludo.
 */
export function heroView(input: { firstName?: string | null; displayName?: string | null; role: UserRole | undefined; now: Date }): HeroView {
  const name = input.firstName?.trim() || input.displayName?.trim() || '';
  // «martes 14 de mayo», como en el diseño: sin la coma que el formato local pone tras el día.
  const date = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long' }).format(input.now).replace(',', '');
  return {
    greeting: name ? `Hola, ${name}` : 'Hola',
    lead: input.role === 'OWNER' || input.role === 'MANAGER'
      ? '¡Qué bueno verte por aquí! Esto es lo que pasa hoy en tu negocio.'
      : '¡Qué bueno verte por aquí!',
    dateLabel: `Hoy · ${date}`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Así va tu negocio · ventas de hoy
// ─────────────────────────────────────────────────────────────────────────────

export interface TodaySalesView {
  amountLabel: string;
  /** «32 ventas». El ticket promedio del diseño no existe en este contrato y no se deriva aquí. */
  salesLabel: string;
  noSalesYet: boolean;
  compare: {
    tone: 'up' | 'down' | 'flat' | 'none';
    /** Lo que va antes de la parte destacada («Hoy vas »). */
    lead: string;
    /** La parte destacada («13 % arriba»). `null` cuando no hay con qué comparar. */
    strong: string | null;
    /** Lo que va después (« del martes pasado ($3,740.00).»), o la frase completa cuando no hay dato. */
    tail: string;
  };
}

/** «de» + «el martes pasado» → «del martes pasado»; «la semana pasada» → «de la semana pasada». */
function ofWeekday(previousLabel: string | null): string {
  const text = weekdayLabel(previousLabel);
  return text.startsWith('el ') ? `del ${text.slice(3)}` : `de ${text}`;
}

/**
 * Ventas de hoy contra el mismo día de la semana pasada. Los números y la decisión de si hay con qué
 * comparar son los de `todayView` (Owner Intelligence): aquí solo cambia la frase, a la del diseño
 * aprobado — «Hoy vas 13 % arriba del martes pasado».
 */
export function todaySalesView(today: OverviewToday | null): TodaySalesView | null {
  const base = todayView(today);
  if (!today || !base) return null;

  if (base.comparisonTone === 'none' || !today.variation || today.previousAmount === null) {
    return {
      amountLabel: base.amountLabel,
      salesLabel: base.salesLabel,
      noSalesYet: base.noSalesYet,
      compare: { tone: 'none', lead: '', strong: null, tail: base.comparisonText },
    };
  }

  const difference = Number(today.variation.amount);
  const previous = formatMoney(today.previousAmount);
  const of = ofWeekday(today.previousLabel);
  if (difference === 0) {
    return {
      amountLabel: base.amountLabel,
      salesLabel: base.salesLabel,
      noSalesYet: base.noSalesYet,
      compare: { tone: 'flat', lead: 'Hoy vas ', strong: 'igual', tail: ` que ${weekdayLabel(today.previousLabel)} (${previous}).` },
    };
  }
  const percentage = today.variation.percentage;
  // Con base cero el porcentaje no significa nada: se dice la diferencia en pesos.
  const magnitude = percentage === null ? formatMoney(Math.abs(difference)) : `${Math.abs(Math.round(percentage))} %`;
  return {
    amountLabel: base.amountLabel,
    salesLabel: base.salesLabel,
    noSalesYet: base.noSalesYet,
    compare: {
      tone: difference > 0 ? 'up' : 'down',
      lead: 'Hoy vas ',
      strong: `${magnitude} ${difference > 0 ? 'arriba' : 'abajo'}`,
      tail: ` ${of} (${previous}).`,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Así va tu negocio · los últimos 7 días
// ─────────────────────────────────────────────────────────────────────────────

export interface WeekBar {
  /** «mié», o «Hoy» para el último día. */
  label: string;
  /** Lo que se lee en voz alta y al pasar el cursor: «miércoles 30: $3,410.00». */
  description: string;
  /** Alto de la barra, de 0 a 100. Es dibujo, no una cifra. */
  heightPercent: number;
  isToday: boolean;
  /** «Hoy» sin ventas todavía: se dibuja un contorno, no una barra en cero. */
  isPending: boolean;
}

export interface WeekBarsView {
  bars: WeekBar[];
  /** «Hoy va en curso» / «Hoy sin ventas aún». */
  status: string;
  /**
   * Los días con ventas netas en negativo, escritos: «Lunes 5: −$120.00. Hubo más devoluciones que
   * ventas.» Sin barra, a la vista serían iguales a un día en $0. `null` si no hay ninguno.
   */
  negativeNote: string | null;
}

/**
 * La semana en barras (P-030). Los totales son los del resumen del Inicio, tal cual; un día en $0 se
 * dibuja en $0 —día sin ventas registradas, no un hueco— y un día negativo (más devoluciones que
 * ventas) no tiene barra, pero su importe se dice a la vista, en una nota bajo la gráfica.
 */
export function weekBarsView(week: HomeWeekDay[]): WeekBarsView | null {
  if (week.length < 2) return null;
  const totals = week.map((day) => Number(day.total));
  const max = Math.max(1, ...totals);
  const lastIndex = week.length - 1;
  const last = week[lastIndex]!;
  const todayPending = Number(last.total) === 0 && last.receipts === 0;

  const bars = week.map<WeekBar>((day, index) => {
    const [year, month, date] = day.date.split('-').map(Number);
    const utc = new Date(Date.UTC(year ?? 2026, (month ?? 1) - 1, date ?? 1));
    const short = new Intl.DateTimeFormat('es-MX', { weekday: 'short', timeZone: 'UTC' }).format(utc).replace('.', '');
    const long = new Intl.DateTimeFormat('es-MX', { weekday: 'long', timeZone: 'UTC' }).format(utc);
    const isToday = index === lastIndex;
    const total = totals[index]!;
    const isPending = isToday && todayPending;
    return {
      label: isToday ? 'Hoy' : short,
      description: isPending ? 'Hoy: sin ventas aún' : `${isToday ? 'Hoy' : `${long} ${date}`}: ${formatMoney(day.total)}`,
      heightPercent: total > 0 ? Math.max(4, Math.round((total / max) * 100)) : 0,
      isToday,
      isPending,
    };
  });

  // Las ventas son netas: las devoluciones restan. Un total negativo es un día con más devoluciones
  // que ventas, y el importe es el del servidor, tal cual.
  const negatives = bars.filter((_, index) => totals[index]! < 0).map((bar) => bar.description);
  const negativeNote = negatives.length > 0 ? `${upperFirst(negatives.join(' · '))}. Hubo más devoluciones que ventas.` : null;

  return { bars, status: todayPending ? 'Hoy sin ventas aún' : 'Hoy va en curso', negativeNote };
}

// ─────────────────────────────────────────────────────────────────────────────
// Así va tu negocio · resultado estimado (B0-01)
// ─────────────────────────────────────────────────────────────────────────────

export interface ResultRow {
  label: string;
  amountLabel: string;
  /** El renglón del resultado: va en negritas y cierra la cuenta. */
  isTotal: boolean;
}

export interface ResultView {
  /** Los cuatro estados del sobre, con nombre de pantalla. No se presentan con la misma certeza. */
  state: 'reliable' | 'estimated' | 'incomplete' | 'blocked';
  /** Lenguaje cotidiano primero (B0-01): el término «Resultado estimado» va en la capa educativa. */
  label: string;
  /** «Octubre, hasta hoy»: el periodo del Inicio es el mes a la fecha. */
  periodLabel: string;
  /** El importe, o `null` cuando no se puede calcular: nunca un «$0.00» en su lugar. */
  amountLabel: string | null;
  /** Solo un número confiable o estimado puede ser la cifra grande. */
  isHeadline: boolean;
  /** «Estimación», cuando el servidor la marca como tal. */
  badge: string | null;
  /** Lo que acompaña o sustituye al número. */
  message: string | null;
  /** «Conozco el costo de $9 de cada $10 que vendes.» — nunca un porcentaje crudo. */
  coverageText: string | null;
  cta: { label: string; destination: string } | null;
  /** «Ver cómo se calcula»: solo cuando hay un número que se sostiene. */
  breakdown: ResultRow[] | null;
  /** La frase que cierra la explicación, según el estado. */
  breakdownNote: string | null;
  /**
   * P-072 (FI-91): «Parte del costo se basa en valores aproximados.» cuando el servidor lo dice y hay
   * una cifra a la vista que calificar; `null` cuando ya va dicho en `message` (una estimación lleva
   * ese supuesto como su frase), cuando no hay cifra, o con un servidor anterior que no lo manda.
   */
  approximateNote: string | null;
}

export const RESULT_TERM = 'Resultado estimado';
export const RESULT_EXPLANATION = 'Es lo que queda de tus ventas después de restar el costo de lo vendido y los gastos confirmados.';
export const RESULT_NOT_ACCOUNTING = 'Es un cálculo estimado, no un corte contable.';

/**
 * El resultado estimado del Inicio, con la presentación que el dueño decidió en B0-01:
 * «Después de costos y gastos: $N aprox.», el mes a la fecha, y la capa educativa aparte.
 *
 * La métrica es la real del servidor —ventas netas − costo de lo vendido − gastos confirmados— y los
 * cuatro renglones de «Ver cómo se calcula» llegan ya calculados: `period.current.netSalesAmount` y
 * los tres campos del sobre. Aquí no se resta nada.
 */
export function resultView(overview: OwnerOverview): ResultView {
  const envelope = overview.operatingResult;
  const view = confidenceView(envelope);
  const period = overview.period;
  const base = {
    label: 'Después de costos y gastos',
    periodLabel: upperFirst(periodLabel(period.fromIso, period.toIso, period.isMonthToDate)),
    coverageText: view.coverageText,
    cta: view.ctaLabel && view.ctaDestination ? { label: view.ctaLabel, destination: view.ctaDestination } : null,
  };

  if (!view.showValue || envelope.value === null) {
    // Sin cifra no hay costo publicado que calificar como aproximado.
    return { ...base, state: 'blocked', amountLabel: null, isHeadline: false, badge: null, message: view.supportText, breakdown: null, breakdownNote: null, approximateNote: null };
  }

  // P-072: lo dice el servidor (nunca se deduce aquí), y solo se repite si `message` no lo dice ya.
  const hasApproximateCost = overview.costEvidence?.hasApproximateCost === true;
  const noteUnless = (message: string | null) => (hasApproximateCost && message !== APPROXIMATE_COST_NOTE ? APPROXIMATE_COST_NOTE : null);

  const amountLabel = formatMoney(envelope.value.amount);
  if (view.tone === 'incomplete') {
    return { ...base, state: 'incomplete', amountLabel, isHeadline: false, badge: null, message: view.supportText, breakdown: null, breakdownNote: null, approximateNote: noteUnless(view.supportText) };
  }

  const breakdown: ResultRow[] = [
    { label: 'Vendiste', amountLabel: formatMoney(period.current.netSalesAmount), isTotal: false },
    { label: 'Lo vendido te costó', amountLabel: `− ${formatMoney(envelope.value.costOfGoodsSoldAmount)}`, isTotal: false },
    { label: 'Otros gastos confirmados', amountLabel: `− ${formatMoney(envelope.value.expensesAmount)}`, isTotal: false },
    { label: 'Después de costos y gastos', amountLabel: `${amountLabel} aprox.`, isTotal: true },
  ];

  if (view.tone === 'estimated') {
    return { ...base, state: 'estimated', amountLabel, isHeadline: true, badge: 'Estimación', message: view.supportText, breakdown, breakdownNote: view.supportText, approximateNote: noteUnless(view.supportText) };
  }
  return { ...base, state: 'reliable', amountLabel, isHeadline: true, badge: null, message: null, breakdown, breakdownNote: 'Antes de impuestos y de lo que retires.', approximateNote: noteUnless(null) };
}

// ─────────────────────────────────────────────────────────────────────────────
// Así va tu negocio · punto de equilibrio
// ─────────────────────────────────────────────────────────────────────────────

export interface BreakEvenView {
  /** `ok` ya cubierto · `short` todavía falta · `unset` sin configurar. */
  state: 'ok' | 'short' | 'unset';
  /** «$883.33/día», o `null` sin configurar. */
  targetLabel: string | null;
  /** «Ya cubierto», «Te faltan $120.00», «Aún sin ventas hoy», o la invitación a configurar. */
  message: string;
  /** Avance hacia la meta, de 0 a 100, con tope: la barra solo responde «¿ya llegué?». */
  progressPercent: number;
  /** El botón que abre la hoja. Solo para quien puede guardarla; si no, `null`. */
  actionLabel: string | null;
}

/**
 * Punto de equilibrio (P-021). Si ya se cubrió o no lo DICE EL SERVIDOR (`breakEvenStatus`); aquí no
 * se vuelve a comparar. La barra es dibujo: ventas de hoy ÷ meta, las dos del mismo resumen.
 *
 * `canConfigure`: la hoja solo la guarda el OWNER (el servidor responde 403 a cualquier otro), así
 * que solo a él se le ofrece el botón. A los demás se les dice el dato, sin una acción imposible.
 */
export function breakEvenView(financial: Pick<HomeFinancial, 'breakEven' | 'breakEvenStatus' | 'breakEvenDifference' | 'salesTotal' | 'receiptsCount'>, canConfigure: boolean): BreakEvenView {
  if (financial.breakEvenStatus === 'NOT_CONFIGURED' || !financial.breakEven.configured) {
    return {
      state: 'unset',
      targetLabel: null,
      message: canConfigure ? 'Configura tu punto de equilibrio' : 'Todavía no está configurado. Lo configura el dueño del negocio.',
      progressPercent: 0,
      actionLabel: canConfigure ? 'Configurar' : null,
    };
  }
  const target = Number(financial.breakEven.dailyTarget);
  const sales = Number(financial.salesTotal);
  const targetLabel = `${formatMoney(financial.breakEven.dailyTarget)}/día`;
  const actionLabel = canConfigure ? 'Ver cálculo' : null;

  if (financial.breakEvenStatus === 'MET' || financial.breakEvenStatus === 'ABOVE') {
    return { state: 'ok', targetLabel, message: 'Ya cubierto', progressPercent: 100, actionLabel };
  }
  const noSalesYet = sales === 0 && financial.receiptsCount === 0;
  const missing = formatMoney(Math.abs(Number(financial.breakEvenDifference ?? 0)));
  const ratio = target > 0 ? sales / target : 0;
  return {
    state: 'short',
    targetLabel,
    message: noSalesYet ? 'Aún sin ventas hoy' : `Te faltan ${missing}`,
    // Nunca llega a 100 aquí: el servidor dijo que todavía falta.
    progressPercent: Math.max(0, Math.min(99, Math.round(ratio * 100))),
    actionLabel,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Así va tu negocio · el mes
// ─────────────────────────────────────────────────────────────────────────────

export interface MonthStripView {
  /** «Octubre hasta hoy:» */
  lead: string;
  amountLabel: string;
  /** «6 % arriba de septiembre al mismo día», o `null` cuando no hay con qué comparar. */
  comparison: string | null;
}

/** «Mayo hasta hoy: $52,960 en ventas · 6 % arriba de abril al mismo día» (P-030). */
export function monthStripView(overview: OwnerOverview): MonthStripView {
  const { period } = overview;
  const [, month] = period.fromIso.split('-').map(Number);
  const lead = period.isMonthToDate
    ? `${upperFirst(MONTHS[(month ?? 1) - 1] ?? '')} hasta hoy:`
    : `${upperFirst(periodLabel(period.fromIso, period.toIso, false))}:`;
  const amountLabel = formatMoney(period.current.netSalesAmount);

  const variation = period.variation?.netSales ?? null;
  if (!variation || !period.previous) return { lead, amountLabel, comparison: null };

  const [, previousMonth] = (period.previousFromIso ?? '').split('-').map(Number);
  const against = period.isMonthToDate && previousMonth
    ? `${MONTHS[previousMonth - 1]} al mismo día`
    : 'el periodo anterior';
  const difference = Number(variation.amount);
  if (difference === 0) return { lead, amountLabel, comparison: `igual que ${against}` };
  const direction = difference > 0 ? 'arriba' : 'abajo';
  const magnitude = variation.percentage === null ? formatMoney(Math.abs(difference)) : `${Math.abs(Math.round(variation.percentage))} %`;
  return { lead, amountLabel, comparison: `${magnitude} ${direction} de ${against}` };
}

// ─────────────────────────────────────────────────────────────────────────────
// Necesita tu atención — lo que se está acabando
// ─────────────────────────────────────────────────────────────────────────────

export interface AttentionItem {
  key: string;
  /** «Mezcla de vainilla alcanza para ~2 días». */
  title: string;
  /** «Estimación preliminar · Centro», o `null`. */
  detail: string | null;
  /** «Registrar compra» o, con existencia negativa, «Ajustar inventario». */
  action: { label: string; destination: string };
}

export interface AttentionView {
  state: 'insufficient-history' | 'clear' | 'items';
  /** Cuántos insumos están en riesgo en total (los que se listan y los que no caben). */
  count: number;
  items: AttentionItem[];
  /** El enlace al inventario: «Ver inventario» o, si hay más de los que caben, «Ver 3 insumo(s) más en Inventario». */
  inventoryLabel: string | null;
  /** La frase del estado sin renglones. */
  message: string | null;
}

/** El mismo texto de días que usa `lowStockRowView`; una prueba vigila que no se separen. */
function daysText(days: number): string {
  if (days < 1) return 'menos de 1 día';
  return days === 1 ? '~1 día' : `~${days} días`;
}

function attentionTitle(item: Pick<LowStockItem, 'ingredientName' | 'state' | 'daysLeft'>): string {
  if (item.state === 'NEGATIVE_STOCK') return `${item.ingredientName}: existencia negativa`;
  if (item.state === 'OUT_OF_STOCK') return `${item.ingredientName}: agotado`;
  return `${item.ingredientName} alcanza para ${daysText(item.daysLeft ?? 0)}`;
}

/**
 * «Necesita tu atención» con lo que el resumen del Inicio ya calcula: los insumos por agotarse, con
 * la misma definición y las mismas acciones de siempre (P-032, B11). Lo que el diseño dibuja y no
 * existe —un corte por revisar, un gasto por confirmar— no aparece.
 */
export function attentionView(lowStock: HomeLowStock, multiStore: boolean): AttentionView {
  if (lowStock.insufficientHistory) {
    return {
      state: 'insufficient-history',
      count: 0,
      items: [],
      inventoryLabel: null,
      message: 'Aún no hay suficiente historial de ventas descontadas para estimar cuántos días durará tu inventario. Sigue descontando tus ventas y esta sección se activará sola.',
    };
  }
  if (lowStock.items.length === 0) {
    return { state: 'clear', count: 0, items: [], inventoryLabel: 'Ver inventario', message: 'Nada por agotarse en los próximos días según tu ritmo de ventas.' };
  }
  const items = lowStock.items.map<AttentionItem>((item) => {
    const row = lowStockRowView(item);
    const details = [
      item.state === 'NEGATIVE_STOCK' ? 'Revisa el inventario' : null,
      item.state === 'ESTIMATE' && item.estimateQuality === 'PRELIMINARY' ? 'Estimación preliminar' : null,
      multiStore ? item.storeName : null,
    ].filter(Boolean);
    return {
      key: `${item.storeId}-${item.ingredientId}`,
      title: attentionTitle(item),
      detail: details.length ? details.join(' · ') : null,
      action: { label: row.actionLabel, destination: row.action === 'purchase' ? 'purchases' : 'inventory/stock' },
    };
  });
  return {
    state: 'items',
    count: lowStock.items.length + lowStock.moreCount,
    items,
    inventoryLabel: lowStock.moreCount > 0 ? `Ver ${lowStock.moreCount} insumo(s) más en Inventario` : 'Ver inventario',
    message: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Por resolver (B0-06)
// ─────────────────────────────────────────────────────────────────────────────

/** A dónde lleva un renglón. `consumption` es el descuento por ventas, que entra con su rango puesto. */
export type PendingTarget =
  | { kind: 'section'; section: AppSection; /** P-075: la vista dentro de la sección (`forms/supervision`). */ view?: string }
  | { kind: 'consumption'; markEntry: boolean; from: string | null };

export interface PendingRow {
  key: string;
  icon: IconName;
  title: string;
  detail: string;
  /** El conteo en una pastilla, o `null` cuando el renglón no lleva una. */
  count: number | null;
  cta: string;
  target: PendingTarget;
  /** Un aviso, no un pendiente: un corte ya cerrado que cambió después. */
  isNotice: boolean;
}

export interface PendingView {
  rows: PendingRow[];
  /** El total que entrega el servidor (OWNER/MANAGER), o `null` cuando no hay nada que contar. */
  total: number | null;
}

export const PENDING_TITLE = 'Por resolver';
export const PENDING_SUPPORT = 'Tienes asuntos pendientes que puedes atender.';
export const PENDING_EMPTY_TITLE = 'Todo al día';
export const PENDING_EMPTY_TEXT = 'No tienes nada pendiente por resolver.';

/** "2026-08-26" → "26 de agosto". La fecha ya viene resuelta en la zona de la tienda. */
function dayLabel(isoDay: string): string {
  const [, month, day] = isoDay.split('-').map(Number);
  if (!month || !day) return isoDay;
  return `${day} de ${MONTHS[month - 1]}`;
}

/**
 * «Por resolver»: el panel de pendientes del Inicio, entero, con el nombre que el dueño decidió en
 * B0-06. Cada persona ve lo que puede atender: los conteos y qué fuente entra los decide el servidor
 * por permisos, y aquí solo se ordenan. Los renglones, sus destinos, sus textos y la condición con la
 * que aparece cada uno son los que ya existían; no se añade ni se quita ninguno.
 *
 * «Te toca contar» es otra cosa —la pantalla de Conteos (F4)— y este bloque no la toca.
 */
export function pendingView(data: PendingSummary, role: UserRole | undefined): PendingView {
  const countsToDo = data.countSheetsToDo ?? 0;

  // EMPLOYEE: solo sus tareas operativas (contar su lista, hacer el corte de su tienda).
  if (role !== 'OWNER' && role !== 'MANAGER') {
    const rows: PendingRow[] = [];
    if (countsToDo > 0) {
      rows.push({
        key: 'counts-mine', icon: 'count', count: null, isNotice: false,
        title: countsToDo === 1 ? 'Tienes una lista de conteo' : `Tienes ${countsToDo} listas de conteo`,
        detail: 'Cuenta lo que hay en el estante y envíala a tu gerente',
        cta: 'Contar', target: { kind: 'section', section: 'counts' },
      });
    }
    if (data.shiftClosePendingToday > 0) {
      rows.push({
        key: 'shift', icon: 'shift', count: null, isNotice: false,
        title: 'Hay ventas de hoy sin corte de turno',
        detail: 'Cierra el turno de tu tienda cuando termine',
        cta: 'Hacer corte', target: { kind: 'section', section: 'shifts' },
      });
    }
    return { rows, total: null };
  }

  const rows: PendingRow[] = [];
  // Primero la operación del día: descontar ventas y el corte. Después, aprobaciones y revisiones.
  if (data.salesToDiscount > 0) {
    rows.push({
      key: 'sales-to-discount', icon: 'inventory', count: data.salesToDiscount, isNotice: false,
      title: `${data.salesToDiscount} venta(s) por descontar`,
      detail: data.salesToDiscountFrom
        ? `Desde el ${dayLabel(data.salesToDiscountFrom)} — baja del inventario los insumos de lo vendido`
        : 'Baja del inventario los insumos de lo vendido',
      cta: 'Descontar ahora', target: { kind: 'consumption', markEntry: true, from: data.salesToDiscountFrom },
    });
  }
  // Este aviso acompaña a las tareas del día y solo sale junto a ellas: es la condición que ya tenía
  // el panel anterior, y se conserva tal cual.
  const hasDailyTasks = data.salesToDiscount > 0 || data.shiftClosePendingToday > 0;
  if (hasDailyTasks && typeof data.autoPostingSkipped === 'number' && data.autoPostingSkipped > 0) {
    rows.push({
      key: 'auto-posting-skipped', icon: 'inventory', count: data.autoPostingSkipped, isNotice: false,
      title: `El descuento automático dejó ${data.autoPostingSkipped} venta(s) fuera`,
      detail: 'Descontó lo que pudo. Estas necesitan que arregles algo antes.',
      cta: 'Ver por qué', target: { kind: 'consumption', markEntry: false, from: null },
    });
  }
  if (data.shiftClosePendingToday > 0) {
    rows.push({
      key: 'shift', icon: 'shift', count: data.shiftClosePendingToday, isNotice: false,
      title: `${data.shiftClosePendingToday} tienda(s) con ventas de hoy sin corte`,
      detail: 'Registra el corte cuando termine el turno',
      cta: 'Hacer corte', target: { kind: 'section', section: 'shifts' },
    });
  }

  const reviews: Array<{ key: string; title: string; detail: string; count: number; icon: IconName; section: AppSection; view?: string }> = [
    { key: 'purchases-approval', title: 'Compras por aprobar', detail: 'Compras enviadas por tu equipo, en espera de tu aprobación', count: data.purchasesPendingApproval, icon: 'purchases', section: 'purchases' },
    { key: 'catalog', title: 'Aprobaciones de catálogo', detail: 'Proveedores/insumos propuestos por tu equipo', count: data.catalogPending, icon: 'check', section: 'catalog-approvals' },
    { key: 'void', title: 'Solicitudes', detail: 'Anulaciones y modificaciones por revisar', count: data.voidRequestsPending, icon: 'void', section: 'void-requests' },
    { key: 'counts', title: 'Conteos por aprobar', detail: 'Lo que contó tu equipo, esperando tu aprobación', count: data.countLinesToApprove ?? 0, icon: 'count', section: 'counts' },
    { key: 'counts-mine', title: 'Listas de conteo para ti', detail: 'Te asignaron contar estos insumos', count: countsToDo, icon: 'count', section: 'counts' },
    { key: 'supervision', title: 'Supervisión de formularios', detail: 'Hallazgos marcados sin revisar', count: data.supervisionUnreviewed, icon: 'reports', section: 'forms', view: 'supervision' },
  ];
  for (const review of reviews) {
    if (review.count <= 0) continue;
    rows.push({ key: review.key, icon: review.icon, count: review.count, isNotice: false, title: review.title, detail: review.detail, cta: 'Revisar', target: review.view ? { kind: 'section', section: review.section, view: review.view } : { kind: 'section', section: review.section } });
  }

  // Aviso, no pendiente: un corte cerrado no se rehace. Fuera del total, igual que en el servidor.
  const touchedCloses = data.shiftClosesTouchedAfterClose ?? 0;
  if (touchedCloses > 0) {
    rows.push({
      key: 'touched-closes', icon: 'shift', count: null, isNotice: true,
      title: touchedCloses === 1 ? 'Un corte cerrado cambió después' : `${touchedCloses} cortes cerrados cambiaron después`,
      detail: 'Se anuló un movimiento en efectivo de ese turno. El corte guardado no cambia; ábrelo para ver la diferencia.',
      cta: 'Ver cortes', target: { kind: 'section', section: 'shifts' },
    });
  }

  return { rows, total: data.total > 0 ? data.total : null };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lo que noté
// ─────────────────────────────────────────────────────────────────────────────

export interface NoteView {
  id: string;
  tone: Tone;
  icon: IconName;
  headline: string;
  meaning: string;
  whyItMatters: string;
  cta: { label: string; destination: string } | null;
}

export interface NotesView {
  /** Nada material: «Todo tranquilo por aquí», con la evidencia de lo revisado. */
  quiet: { headline: string; evidence: string } | null;
  notes: NoteView[];
  moreLabel: string | null;
}

// El color acompaña a la categoría que decide el servidor; el significado lo lleva siempre el texto.
// El verde queda para las buenas noticias (B0-08).
const NOTE_TONE: Record<InsightCategory, { tone: Tone; icon: IconName }> = {
  RISK: { tone: 'warning', icon: 'warning' },
  MONEY: { tone: 'info', icon: 'expenses' },
  OPPORTUNITY: { tone: 'accent', icon: 'info' },
  DISCOVERY: { tone: 'accent', icon: 'info' },
  GOOD_NEWS: { tone: 'success', icon: 'success' },
  OPERATIONAL: { tone: 'highlight', icon: 'shift' },
};

/**
 * «Lo que noté» con lo que el motor de insights nota HOY: hasta tres mensajes, cada uno con su
 * titular, qué significa, por qué importa y a dónde ir. Los insights de descubrimiento del diseño
 * (el producto más vendido, la hora fuerte, el patrón de seis semanas) no existen todavía y no se
 * escriben aquí (P-032, B13; FUTURE_UPGRADES #32 y #38).
 */
export function notesView(overview: OwnerOverview): NotesView {
  const view = insightsView(overview);
  if (view.allQuiet) return { quiet: { headline: view.quietHeadline, evidence: view.quietEvidence }, notes: [], moreLabel: null };
  return {
    quiet: null,
    notes: overview.insights.selected.map((insight) => ({
      id: insight.id,
      tone: NOTE_TONE[insight.category]?.tone ?? 'info',
      icon: NOTE_TONE[insight.category]?.icon ?? 'info',
      headline: insight.headline,
      meaning: insight.meaning,
      whyItMatters: insight.whyItMatters,
      cta: insight.cta,
    })),
    moreLabel: view.moreLabel,
  };
}
